import api from "./client";
import { toast } from "sonner";
import type {
  AdminUser,
  AuthMe,
  Category,
  CategoryFilter,
  Contact,
  ContactSearchHit,
  Document,
  DocumentDetail,
  DocumentList,
  DocumentPatch,
  FsListing,
  FsRoot,
  LoginResponse,
  MaterializeResponse,
  PipelineRun,
  SavedView,
  ScanEnvironment,
  ScanEnvironmentDetail,
  ScanEnvironmentEntry,
  SettingEntry,
  Stats,
  RentalUnit,
  RentLedger,
  RentLedgerEntry,
  RentLedgerCellDetail,
  RentStatementResult,
  IbanSuggestionResult,
  ReportObject,  ReportTemplate,
  ReportTemplateField,
  Taxpayer,
  TaxObject,
  TaxpayerTypeCategory,
  TaxpayerTypeKind,
  Template,
  TemplateDetail,
  TenantInfo,
  TenantUpdate,
  Todo,
  UserRole,
  AssistantProviderInfo,
  AssistantConversationSummary,
  AssistantConversationDetail,
  AssistantScope,
  AssistantRunState,
  AssistantFile,
  ReceiptLink,
  ReceiptCandidate,
  SearchResponse,
  SearchExplain,
  SearchHelp,
} from "@/types/api";

export type { Taxpayer };

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authApi = {
  login: (email: string, password: string) =>
    api
      .post<LoginResponse>("/auth/login", { email, password })
      .then((r) => r.data),
  refresh: () =>
    api.post<LoginResponse>("/auth/refresh").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get<AuthMe>("/auth/me").then((r) => r.data),
  changePassword: (oldPassword: string, newPassword: string) =>
    api
      .post("/auth/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Stats / Dashboard
// ---------------------------------------------------------------------------

export const statsApi = {
  get: () => api.get<Stats>("/stats").then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Stammdaten — Lesen
// ---------------------------------------------------------------------------

export const stammdatenApi = {
  taxpayers: () => api.get<Taxpayer[]>("/taxpayers").then((r) => r.data),
  taxObjects: (taxpayerId?: number) =>
    api
      .get<TaxObject[]>("/tax-objects", {
        params: taxpayerId ? { taxpayer_id: taxpayerId } : undefined,
      })
      .then((r) => r.data),
  categories: (params?: { taxpayer_id?: number; template_id?: number; scan_environment_id?: number; q?: string; include_blocked?: boolean; include_deleted?: boolean }) =>
    api
      .get<Category[]>("/categories", { params })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Stammdaten — CRUD
// ---------------------------------------------------------------------------

export interface TaxpayerInput {
  /** Optional: wenn nicht gesetzt, generiert das Backend automatisch aus Firma/Name. */
  slug?: string;
  organization?: string | null;
  name?: string;
  type_category_id?: number;
  type_kind_id?: number | null;
  tax_id?: string | null;
  notes?: string | null;
  source_path?: string | null;
  target_path?: string | null;
}

export const taxpayersApi = {
  create: (body: Required<Pick<TaxpayerInput, "name" | "type_category_id">> & TaxpayerInput) =>
    api.post<Taxpayer>("/taxpayers", body).then((r) => r.data),
  update: (id: number, body: TaxpayerInput) =>
    api.put<Taxpayer>(`/taxpayers/${id}`, body).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/taxpayers/${id}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Taxpayer-Typen (Kategorien + Arten)
// ---------------------------------------------------------------------------

export interface TaxpayerTypeCategoryInput {
  slug?: string;
  label?: string;
  sort_order?: number;
}

export interface TaxpayerTypeKindInput {
  slug?: string;
  label?: string;
  sort_order?: number;
}

export const taxpayerTypesApi = {
  listCategories: () =>
    api.get<TaxpayerTypeCategory[]>("/taxpayer-types/categories").then((r) => r.data),
  createCategory: (body: Required<Pick<TaxpayerTypeCategoryInput, "slug" | "label">> & TaxpayerTypeCategoryInput) =>
    api.post<TaxpayerTypeCategory>("/taxpayer-types/categories", body).then((r) => r.data),
  updateCategory: (id: number, body: TaxpayerTypeCategoryInput) =>
    api.put<TaxpayerTypeCategory>(`/taxpayer-types/categories/${id}`, body).then((r) => r.data),
  deleteCategory: (id: number) =>
    api.delete(`/taxpayer-types/categories/${id}`).then((r) => r.data),
  createKind: (categoryId: number, body: Required<Pick<TaxpayerTypeKindInput, "slug" | "label">> & TaxpayerTypeKindInput) =>
    api.post<TaxpayerTypeKind>(`/taxpayer-types/categories/${categoryId}/kinds`, body).then((r) => r.data),
  updateKind: (kindId: number, body: TaxpayerTypeKindInput) =>
    api.put<TaxpayerTypeKind>(`/taxpayer-types/kinds/${kindId}`, body).then((r) => r.data),
  deleteKind: (kindId: number) =>
    api.delete(`/taxpayer-types/kinds/${kindId}`).then((r) => r.data),
};

export interface TaxObjectInput {
  taxpayer_id?: number;
  code?: string;
  label?: string;
  object_number?: string | null;
  street?: string | null;
  postcode?: string | null;
  city?: string | null;
  country?: string | null;
  notes?: string | null;
}

export const taxObjectsApi = {
  create: (body: Required<Pick<TaxObjectInput, "taxpayer_id" | "label">> & TaxObjectInput) =>
    api.post<TaxObject>("/tax-objects", body).then((r) => r.data),
  update: (id: number, body: TaxObjectInput) =>
    api.put<TaxObject>(`/tax-objects/${id}`, body).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/tax-objects/${id}`).then((r) => r.data),
  nextCode: (taxpayerId: number) =>
    api
      .get<{ code: string }>("/tax-objects/next-code", {
        params: { taxpayer_id: taxpayerId },
      })
      .then((r) => r.data.code),
};

export interface CategoryInput {
  template_id?: number;
  parent_id?: number | null;
  code?: string;
  name?: string;
  sort_order?: number;
  per_object?: boolean;
  keywords?: string | null;
  /** OP-6: beim Anlegen automatisch passenden Standard-Filter mit anlegen. */
  seed_standard_filter?: boolean;
}

export const categoriesApi = {
  create: (body: Required<Pick<CategoryInput, "template_id" | "code" | "name">> & CategoryInput) =>
    api.post<Category>("/categories", body).then((r) => r.data),
  update: (id: number, body: CategoryInput & { is_blocked?: boolean }) =>
    api.put<Category>(`/categories/${id}`, body).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/categories/${id}`).then((r) => r.data),
  block: (id: number, blocked: boolean) =>
    api.post(`/categories/${id}/block`, { blocked }).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export interface TemplateInput {
  slug?: string;
  name?: string;
  description?: string | null;
  structure?: Record<string, unknown>;
}

export const templatesApi = {
  list: () => api.get<Template[]>("/templates").then((r) => r.data),
  get: (id: number) =>
    api.get<TemplateDetail>(`/templates/${id}`).then((r) => r.data),
  create: (body: Required<Pick<TemplateInput, "slug" | "name">> & TemplateInput) =>
    api.post<Template>("/templates", body).then((r) => r.data),
  update: (id: number, body: TemplateInput) =>
    api.put<Template>(`/templates/${id}`, body).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/templates/${id}`).then((r) => r.data),
  assign: (templateId: number, taxpayerId: number) =>
    api
      .post("/templates/assignments", {
        template_id: templateId,
        taxpayer_id: taxpayerId,
      })
      .then((r) => r.data),
  unassign: (assignmentId: number) =>
    api.delete(`/templates/assignments/${assignmentId}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export interface DocumentFilters {
  taxpayer_id?: number;
  tax_object_id?: number;
  abrechnungsjahr?: number;
  state?: string;
  source?: string;
  quelle?: string;
  processed_file_state?: string;
  q?: string;
  q_and?: string;
  q_or?: string;
  q_not?: string;
  search_fields?: string;
  datum_from?: string;
  datum_to?: string;
  created_from?: string;
  created_to?: string;
  sort?: string;
  order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export interface DocumentNeighbors {
  current_id: number;
  prev_id: number | null;
  next_id: number | null;
  position: number | null;
  total: number;
  in_filter: boolean;
}

export const documentsApi = {
  list: (filters: DocumentFilters = {}) =>
    api
      .get<DocumentList>("/documents", { params: filters })
      .then((r) => r.data),
  get: (id: number) =>
    api.get<DocumentDetail>(`/documents/${id}`).then((r) => r.data),
  neighbors: (id: number, filters: DocumentFilters = {}) =>
    api
      .get<DocumentNeighbors>(`/documents/${id}/neighbors`, { params: filters })
      .then((r) => r.data),
  ids: (filters: DocumentFilters = {}, limit = 5000) =>
    api
      .get<{ document_ids: number[]; total: number }>(`/documents/ids`, {
        params: { ...filters, limit },
      })
      .then((r) => r.data),
  patch: (id: number, body: DocumentPatch) =>
    api.patch<Document>(`/documents/${id}`, body).then((r) => r.data),
  refile: (id: number) =>
    api
      .post<{ status: string; info: string | null; filed_path: string | null }>(
        `/documents/${id}/refile`
      )
      .then((r) => r.data),
  fileUrl: (id: number) => `/api/documents/${id}/file`,
  /** Holt die Datei via Axios (mit Bearer + Tenant-Header) und öffnet als Blob-URL in neuem Tab. */
  openInTab: async (id: number) => {
    try {
      const res = await api.get(`/documents/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      const win = window.open(url, "_blank", "noopener");
      // Blob-URL nach 1 Minute freigeben — bis dahin sollte der neue Tab geladen sein
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (!win) {
        // Popup blockiert → wenigstens als Download anbieten
        const a = document.createElement("a");
        a.href = url;
        a.download = `document-${id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e: any) {
      if (e?.response?.status === 410) {
        toast.error("Die Originaldatei existiert nicht mehr auf dem Server (410 Gone).");
      } else {
        toast.error("Datei konnte nicht geladen werden.");
      }
    }
  },
  /** Holt die Datei als Blob-URL (für inline PDF-Vorschau). */
  downloadBlobUrl: async (id: number): Promise<string> => {
    const res = await api.get(`/documents/${id}/file`, { responseType: "blob" });
    return URL.createObjectURL(res.data as Blob);
  },
  /** Mehrere Dateien hochladen (Postbox → Neu). */
  upload: (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return api
      .post<{ created: number; skipped: number; skipped_files: string[]; document_ids: number[] }>(
        "/documents/upload",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((r) => r.data);
  },
};

// ---------------------------------------------------------------------------
// Todos
// ---------------------------------------------------------------------------

export interface ResolveTodoBody {
  taxpayer_id?: number;
  tax_object_id?: number;
  category_id?: number;
  confirm_current?: boolean;
  note?: string;
}

export interface TodoListParams {
  taxpayer_id?: number;
  reason?: string;
  limit?: number;
  q?: string;
  q_and?: string;
  q_or?: string;
  q_not?: string;
  sort?: string;
  order?: "asc" | "desc";
}

export const todosApi = {
  list: (params: TodoListParams = {}) =>
    api
      .get<Todo[]>("/todos", {
        params: {
          limit: params.limit ?? 200,
          taxpayer_id: params.taxpayer_id,
          reason: params.reason,
          q: params.q,
          q_and: params.q_and,
          q_or: params.q_or,
          q_not: params.q_not,
          sort: params.sort,
          order: params.order,
        },
      })
      .then((r) => r.data),
  documentIds: (params: TodoListParams = {}) =>
    api
      .get<{ document_ids: number[]; total: number }>("/todos/document-ids", {
        params: {
          limit: params.limit ?? 1000,
          taxpayer_id: params.taxpayer_id,
          reason: params.reason,
          q: params.q,
          q_and: params.q_and,
          q_or: params.q_or,
          q_not: params.q_not,
          sort: params.sort,
          order: params.order,
        },
      })
      .then((r) => r.data),
  resolve: (id: number, body: ResolveTodoBody) =>
    api.post(`/todos/${id}/resolve`, body).then((r) => r.data),
  dismiss: (id: number, note?: string) =>
    api.post(`/todos/${id}/dismiss`, { note }).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Triggers + Pipeline
// ---------------------------------------------------------------------------

export interface TriggerResult {
  run_id: number;
  stage: string;
  status: string;
  tenant_id: number;
  scan_environment_id?: number | null;
}

export interface TriggerOptions {
  limit?: number;
  scan_environment_id?: number;
}

function buildTriggerBody(opts: TriggerOptions = {}) {
  const body: Record<string, unknown> = {};
  if (opts.limit !== undefined) body.limit = opts.limit;
  if (opts.scan_environment_id !== undefined)
    body.scan_environment_id = opts.scan_environment_id;
  return body;
}

export const triggersApi = {
  discover: (opts?: TriggerOptions) =>
    api.post<TriggerResult>("/triggers/discover", buildTriggerBody(opts)).then((r) => r.data),
  ocr: (opts?: TriggerOptions) =>
    api.post<TriggerResult>("/triggers/ocr", buildTriggerBody(opts)).then((r) => r.data),
  extract: (opts?: TriggerOptions) =>
    api.post<TriggerResult>("/triggers/extract", buildTriggerBody(opts)).then((r) => r.data),
  classify: (opts?: TriggerOptions) =>
    api.post<TriggerResult>("/triggers/classify", buildTriggerBody(opts)).then((r) => r.data),
  file: (opts?: TriggerOptions) =>
    api.post<TriggerResult>("/triggers/file", buildTriggerBody(opts)).then((r) => r.data),
  process: (opts?: TriggerOptions) =>
    api.post<TriggerResult>("/triggers/process", buildTriggerBody(opts)).then((r) => r.data),

  /**
   * OCR-Nachverarbeitung erneut starten.
   *
   * `scope`: "selected" für einzelne/ausgewählte Dokumente (dann sind
   * `documentIds` Pflicht), "missing" für alle ohne OCR-Text, "all" für alle.
   */
  ocrRequeue: (
    scope: "selected" | "missing" | "all",
    documentIds?: number[],
    limit?: number
  ) =>
    api
      .post<TriggerResult & { requeued: number; message: string }>(
        "/triggers/ocr/requeue",
        {
          scope,
          ...(documentIds ? { document_ids: documentIds } : {}),
          ...(limit ? { limit } : {}),
        }
      )
      .then((r) => r.data),

  /**
   * Erkennung nur für die noch nicht erkannten Dokumente neu starten
   * (OCR → Extraktion → Klassifikation).
   */
  recognizeMissing: (opts?: TriggerOptions) =>
    api
      .post<TriggerResult & { requeued: number; message: string }>(
        "/triggers/recognize/missing",
        buildTriggerBody(opts)
      )
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Scan Environments
// ---------------------------------------------------------------------------

export interface ScanEnvironmentInput {
  /** Beim Update: nur setzen, wenn Slug wirklich umbenannt werden soll. */
  slug?: string;
  name?: string;
  description?: string | null;
  path_prefix?: string;
}

export interface EntryCreateInput {
  taxpayer_id: number;
  template_source: "new" | "clone" | "existing";
  template_name?: string;
  clone_template_id?: number;
  existing_template_id?: number;
}

// ---------------------------------------------------------------------------
// Category-Filter (Suchregeln pro Kategorie)
// ---------------------------------------------------------------------------

export interface CategoryFilterInput {
  name?: string;
  source_folder?: string | null;
  patterns_raw?: string;
  patterns?: Array<{ type: string; value: string }>;
  sort_order?: number;
  is_active?: boolean;
}

export const categoryFiltersApi = {
  list: (envId: number, categoryId: number) =>
    api
      .get<CategoryFilter[]>(
        `/scan-environments/${envId}/categories/${categoryId}/filters`
      )
      .then((r) => r.data),
  create: (envId: number, categoryId: number, body: Required<Pick<CategoryFilterInput, "name">> & CategoryFilterInput) =>
    api
      .post<CategoryFilter>(
        `/scan-environments/${envId}/categories/${categoryId}/filters`,
        body
      )
      .then((r) => r.data),
  update: (envId: number, filterId: number, body: CategoryFilterInput) =>
    api
      .put<CategoryFilter>(`/scan-environments/${envId}/filters/${filterId}`, body)
      .then((r) => r.data),
  delete: (envId: number, filterId: number) =>
    api.delete(`/scan-environments/${envId}/filters/${filterId}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Tenant (eigener Mandant)
// ---------------------------------------------------------------------------

export const tenantApi = {
  me: () => api.get<TenantInfo>("/tenants/me").then((r) => r.data),
  update: (body: TenantUpdate) =>
    api.put<TenantInfo>("/tenants/me", body).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Filesystem-Browser
// ---------------------------------------------------------------------------

export const filesystemApi = {
  roots: () => api.get<FsRoot[]>("/filesystem/roots").then((r) => r.data),
  list: (path: string) =>
    api
      .get<FsListing>("/filesystem/list", { params: { path } })
      .then((r) => r.data),
  reveal: (path: string) =>
    api
      .post<{ status: string; path: string; system: string }>(
        "/filesystem/reveal",
        { path }
      )
      .then((r) => r.data),
  fileUrl: (path: string) =>
    `/api/filesystem/file?path=${encodeURIComponent(path)}`,
  /** Lädt die Datei via Axios (mit Bearer + Tenant-Header) und gibt Blob-URL zurück. */
  fetchBlobUrl: async (path: string): Promise<string> => {
    const res = await api.get("/filesystem/file", {
      params: { path },
      responseType: "blob",
    });
    return URL.createObjectURL(res.data as Blob);
  },
  openInTab: async (path: string) => {
    try {
      const res = await api.get("/filesystem/file", {
        params: { path },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data as Blob);
      const win = window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = path.split("/").pop() || "file";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e: any) {
      if (e?.response?.status === 410) {
        toast.error("Die Datei existiert nicht mehr auf dem Server (410 Gone).");
      } else {
        toast.error("Datei konnte nicht geöffnet werden.");
      }
    }
  },
};

export const scanEnvironmentsApi = {
  list: () =>
    api.get<ScanEnvironment[]>("/scan-environments").then((r) => r.data),
  get: (id: number) =>
    api.get<ScanEnvironmentDetail>(`/scan-environments/${id}`).then((r) => r.data),
  create: (body: Required<Pick<ScanEnvironmentInput, "slug" | "name" | "path_prefix">> & ScanEnvironmentInput) =>
    api.post<ScanEnvironment>("/scan-environments", body).then((r) => r.data),
  update: (id: number, body: ScanEnvironmentInput) =>
    api.put<ScanEnvironment>(`/scan-environments/${id}`, body).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/scan-environments/${id}`).then((r) => r.data),
  clone: (id: number, body: ScanEnvironmentInput = {}) =>
    api.post<ScanEnvironment>(`/scan-environments/${id}/clone`, body).then((r) => r.data),
  lock: (id: number) =>
    api.post<ScanEnvironment>(`/scan-environments/${id}/lock`).then((r) => r.data),
  unlock: (id: number) =>
    api.post<ScanEnvironment>(`/scan-environments/${id}/unlock`).then((r) => r.data),
  setDefault: (id: number) =>
    api.post<ScanEnvironment>(`/scan-environments/${id}/set-default`).then((r) => r.data),
  createEntry: (envId: number, body: EntryCreateInput) =>
    api.post<ScanEnvironmentEntry>(`/scan-environments/${envId}/entries`, body).then((r) => r.data),
  updateEntry: (envId: number, entryId: number, templateId: number) =>
    api
      .put<ScanEnvironmentEntry>(`/scan-environments/${envId}/entries/${entryId}`, {
        template_id: templateId,
      })
      .then((r) => r.data),
  deleteEntry: (envId: number, entryId: number) =>
    api.delete(`/scan-environments/${envId}/entries/${entryId}`).then((r) => r.data),
  materialize: (envId: number) =>
    api
      .post<MaterializeResponse>(`/scan-environments/${envId}/materialize`)
      .then((r) => r.data),
};

export const pipelineApi = {
  runs: (params?: { stage?: string; status?: string; limit?: number }) =>
    api.get<PipelineRun[]>("/pipeline/runs", { params }).then((r) => r.data),
  activeRuns: () =>
    api.get<PipelineRun[]>("/pipeline/runs/active").then((r) => r.data),
  get: (id: number) =>
    api.get<PipelineRun>(`/pipeline/runs/${id}`).then((r) => r.data),
  cancel: (id: number) =>
    api.post<PipelineRun>(`/pipeline/runs/${id}/cancel`).then((r) => r.data),
  cancelStale: () =>
    api
      .post<{ cancelled: number[]; count: number }>("/pipeline/runs/cancel-stale")
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Views + Export
// ---------------------------------------------------------------------------

export const viewsApi = {
  list: () => api.get<SavedView[]>("/views").then((r) => r.data),
  export: (slug: string) =>
    api
      .post(`/views/${slug}/export`, null, { responseType: "blob" })
      .then((r) => ({
        blob: r.data as Blob,
        filename:
          extractFilename(r.headers["content-disposition"]) ||
          `${slug}.xlsx`,
      })),
};

function extractFilename(contentDisposition?: string): string | null {
  if (!contentDisposition) return null;
  const mStar = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(contentDisposition);
  if (mStar) return decodeURIComponent(mStar[1].replace(/["']/g, "").trim());
  const m = /filename=(?:"([^"]+)"|'([^']+)'|([^;\s]+))/i.exec(contentDisposition);
  if (m) return decodeURIComponent((m[1] || m[2] || m[3] || "").trim());
  return null;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface SettingOptions {
  chat_models: string[];
  ocr_models: string[];
  ocr_providers: string[];
}

export const settingsApi = {
  list: () => api.get<SettingEntry[]>("/settings").then((r) => r.data),
  options: () =>
    api.get<SettingOptions>("/settings/_options").then((r) => r.data),
  get: (key: string) =>
    api.get<{ key: string; value: string | null }>(`/settings/${key}`).then((r) => r.data),
  set: (key: string, value: string, valueType: string = "string") =>
    api
      .put<{ key: string; value: string }>(`/settings/${key}`, {
        value,
        value_type: valueType,
      })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Admin / Users
// ---------------------------------------------------------------------------

export interface AdminUserCreate {
  email: string;
  password: string;
  full_name?: string | null;
  is_superuser?: boolean;
  tenants?: Array<{ tenant_slug: string; role: UserRole }>;
}

export interface AdminUserUpdate {
  full_name?: string | null;
  is_active?: boolean;
  is_superuser?: boolean;
}

export const adminUsersApi = {
  list: () => api.get<AdminUser[]>("/admin/users").then((r) => r.data),
  get: (id: number) =>
    api.get<AdminUser>(`/admin/users/${id}`).then((r) => r.data),
  create: (body: AdminUserCreate) =>
    api.post<AdminUser>("/admin/users", body).then((r) => r.data),
  update: (id: number, body: AdminUserUpdate) =>
    api.put<AdminUser>(`/admin/users/${id}`, body).then((r) => r.data),
  delete: (id: number) =>
    api.delete(`/admin/users/${id}`).then((r) => r.data),
  resetPassword: (id: number, newPassword: string) =>
    api
      .post(`/admin/users/${id}/reset-password`, { new_password: newPassword })
      .then((r) => r.data),
  grant: (id: number, tenantSlug: string, role: UserRole) =>
    api
      .post(`/admin/users/${id}/grants`, {
        tenant_slug: tenantSlug,
        role,
      })
      .then((r) => r.data),
  revoke: (userId: number, tenantId: number) =>
    api.delete(`/admin/users/${userId}/grants/${tenantId}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Contacts (Kontakte / Stammdaten)
// ---------------------------------------------------------------------------

export interface ContactInput {
  kind?: "business" | "private" | "both";
  salutation?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  general_name?: string | null;
  street?: string | null;
  house_number?: string | null;
  house_number_suffix?: string | null;
  postcode?: string | null;
  city?: string | null;
  city_suffix?: string | null;
  state?: string | null;
  country?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  notes?: string | null;
  bank_accounts?: Array<{ iban?: string | null; swift?: string | null; bank_name?: string | null }>;
  identifiers?: Array<{
    taxpayer_id?: number | null;
    customer_number?: string | null;
    debitor_number?: string | null;
    creditor_number?: string | null;
    mandant_number?: string | null;
    object_number?: string | null;
    meter_number?: string | null;
  }>;
}

export interface ContactListParams {
  q?: string;
  limit?: number;
  include_blocked?: boolean;
  include_deleted?: boolean;
}

export const contactsApi = {
  list: (params: ContactListParams = {}) =>
    api.get<Contact[]>("/contacts", { params }).then((r) => r.data),
  search: (q: string, limit = 10) =>
    api.get<ContactSearchHit[]>("/contacts/search", { params: { q, limit } }).then((r) => r.data),
  get: (id: number) => api.get<Contact>(`/contacts/${id}`).then((r) => r.data),
  create: (body: ContactInput) =>
    api.post<Contact>("/contacts", body).then((r) => r.data),
  update: (id: number, body: ContactInput) =>
    api.put<Contact>(`/contacts/${id}`, body).then((r) => r.data),
  delete: (id: number) => api.delete(`/contacts/${id}`).then((r) => r.data),
  block: (id: number, blocked: boolean) =>
    api.post(`/contacts/${id}/block`, { blocked }).then((r) => r.data),
  quick: (text: string, sourceDocumentId?: number) =>
    api
      .post<Contact>("/contacts/quick", {
        text,
        source_document_id: sourceDocumentId,
      })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Banking4-Import & Master-Viewer
// ---------------------------------------------------------------------------

export interface BankTransaction {
  id: number;
  oid: string;
  owner_acct_name: string | null;
  owner_acct_iban: string | null;
  bank_name?: string | null;
  booking_date: string | null;
  value_date: string | null;
  payee_payer_name: string | null;
  payee_payer_acct_holder_name: string | null;
  payee_payer_iban: string | null;
  purpose: string | null;
  entry_text: string | null;
  category: string | null;
  currency_code: string | null;
  credit_amount: string | null;
  debit_amount: string | null;
  value: string | null;
  entry_balance: string | null;
  gvc: string | null;
  bank_import_id: number;
  scan_environment_id: number | null;
  raw_json?: Record<string, string>;
  row_hash?: string;
  // Nur in der Detailansicht (GET /bank/transactions/{id}) befüllt:
  owner_acct_no?: string | null;
  entry_date?: string | null;
  end_to_end_id?: string | null;
  mandate_id?: string | null;
  creditor_id?: string | null;
  notes?: string | null;
  booking_status_code?: string | null;
}

export interface BankTransactionList {
  total: number;
  page: number;
  page_size: number;
  sum_credit: string;
  sum_debit: string;
  sum_value: string;
  items: BankTransaction[];
}

export interface BankTransactionFilters {
  owner_acct_iban?: string;
  owner_acct_ibans?: string[];
  owner_acct_name?: string;
  owner_id?: number;
  owner_ids?: number[];
  bank_ids?: number[];
  owner_side?: "both" | "own" | "counter";
  category?: string;
  scan_environment_id?: number;
  bank_import_id?: number;
  datum_from?: string;
  datum_to?: string;
  value_min?: string;
  value_max?: string;
  flow?: "credit" | "debit";
  amount_op?: "gt" | "ge" | "lt" | "le" | "eq" | "ne";
  amount?: string;
  q?: string;
  q_and?: string;
  q_or?: string;
  q_not?: string;
  sort?: string;
  order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export interface BankAccount {
  owner_acct_iban: string | null;
  owner_acct_name: string | null;
  count: number;
  sum_credit: string;
  sum_debit: string;
}

/** Verfügbare Filterwerte unter den aktuellen Such-/Zeitraumfiltern. */
export interface BankFacets {
  accounts: Array<{
    owner_acct_iban: string | null;
    owner_acct_name: string | null;
    count: number;
  }>;
  owner_ids: number[];
  flows: { credit: boolean; debit: boolean };
}

/** Kontoinhaber (ROH) inkl. zugeordneter IBANs. */
export interface BankAccountOwner {
  id: number;
  name: string;
  kind: string | null;
  notes: string | null;
  ibans: string[];
  iban_count: number;
}

/** Eine vorkommende IBAN (eigene oder Gegenseite) mit importierter Bezeichnung. */
export interface BankIbanAccount {
  iban: string;
  imported_name: string | null;
  imported_names: string[];
  count: number;
  owner_id: number | null;
  owner_name: string | null;
  bic?: string | null;
  bank_name?: string | null;
}

export interface BankGroupResult {
  by: string;
  groups: Array<{
    key: string | null;
    count: number;
    sum_credit: string;
    sum_debit: string;
    sum_value: string;
  }>;
}

export interface BankImportRun {
  id: number;
  filename: string;
  status: string;
  total_rows: number;
  inserted_rows: number;
  duplicate_rows: number;
  error_rows: number;
  sum_credit: string;
  sum_debit: string;
  scan_environment_id: number | null;
  source_type?: string | null;
  created_at: string | null;
  error_message: string | null;
}

export interface RevolutAnalyzeResult extends BankAnalyzeResult {
  iban?: string | null;
  accounts?: string[];
  payers?: string[];
  types?: Record<string, number>;
  final_balance?: string | null;
}

export interface BankAnalyzeResult {
  columns: number;
  total_rows: number;
  rows_with_wrong_column_count: number;
  unique_accounts: number;
  unique_owners: number;
  date_min: string | null;
  date_max: string | null;
  sum_credit: string;
  sum_debit: string;
}

export interface BankImportResult {
  bank_import_id: number | null;
  filename?: string;
  total_rows: number;
  inserted_rows: number;
  duplicate_rows: number;
  error_rows: number;
  sum_credit: string;
  sum_debit: string;
  errors: string[];
}

export interface BankSqlResult {
  columns: string[];
  rows: Array<Array<string | number | null>>;
  row_count: number;
  truncated: boolean;
}

export interface BankNl2SqlResult {
  understanding: string;
  sql: string;
  model: string;
  warnings: string[];
}

export interface BankEnvironment {
  scan_environment_id: number | null;
  slug: string | null;
  name: string;
  count: number;
}

export interface BankExportRequest {
  ids?: number[];
  filters?: BankTransactionFilters;
  konto?: string;
  gegenkonto?: string;
  berater?: string;
  mandant?: string;
}
export const bankApi = {
  list: (filters: BankTransactionFilters = {}) =>
    api
      .get<BankTransactionList>("/bank/transactions", {
        params: filters,
        paramsSerializer: { indexes: null },
      })
      .then((r) => r.data),
  get: (id: number) =>
    api.get<BankTransaction>(`/bank/transactions/${id}`).then((r) => r.data),
  accounts: () => api.get<BankAccount[]>("/bank/accounts").then((r) => r.data),
  facets: (filters: BankTransactionFilters = {}) =>
    api
      .get<BankFacets>("/bank/transactions/facets", {
        params: filters,
        paramsSerializer: { indexes: null },
      })
      .then((r) => r.data),
  environments: () =>
    api.get<BankEnvironment[]>("/bank/environments").then((r) => r.data),
  // --- Kontoinhaber-Zuordnung ---
  owners: () => api.get<BankAccountOwner[]>("/bank/owners").then((r) => r.data),
  ownerMap: () =>
    api.get<Record<string, string>>("/bank/owner-map").then((r) => r.data),
  ibanAccounts: () =>
    api.get<BankIbanAccount[]>("/bank/iban-accounts").then((r) => r.data),
  createOwner: (body: { name: string; kind?: string | null; notes?: string | null }) =>
    api.post<BankAccountOwner>("/bank/owners", body).then((r) => r.data),
  updateOwner: (
    id: number,
    body: { name?: string; kind?: string | null; notes?: string | null },
  ) => api.patch<BankAccountOwner>(`/bank/owners/${id}`, body).then((r) => r.data),
  deleteOwner: (id: number) =>
    api.delete<{ deleted: number }>(`/bank/owners/${id}`).then((r) => r.data),
  assignIbans: (ownerId: number, ibans: string[]) =>
    api
      .post<BankAccountOwner>(`/bank/owners/${ownerId}/ibans`, { ibans })
      .then((r) => r.data),
  unassignIbans: (ibans: string[]) =>
    api
      .delete<{ unassigned: string[] }>("/bank/owner-ibans", { data: { ibans } })
      .then((r) => r.data),
  exportDatev: (body: BankExportRequest) =>
    api
      .post("/bank/export", body, { responseType: "blob" })
      .then((r) => ({
        blob: r.data as Blob,
        filename:
          extractFilename(r.headers["content-disposition"]) ||
          "EXTF_Buchungsstapel.csv",
      })),
  group: (by: string, limit = 200) =>
    api
      .get<BankGroupResult>("/bank/group", { params: { by, limit } })
      .then((r) => r.data),
  imports: () => api.get<BankImportRun[]>("/bank/imports").then((r) => r.data),
  analyze: (file: File, encoding = "cp1252") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("encoding", encoding);
    return api
      .post<BankAnalyzeResult>("/bank/analyze", fd)
      .then((r) => r.data);
  },
  import: (
    file: File,
    opts: { scan_environment_slug?: string; scan_environment_id?: number; encoding?: string } = {}
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.scan_environment_slug)
      fd.append("scan_environment_slug", opts.scan_environment_slug);
    if (opts.scan_environment_id != null)
      fd.append("scan_environment_id", String(opts.scan_environment_id));
    fd.append("encoding", opts.encoding ?? "cp1252");
    return api
      .post<BankImportResult>("/bank/import", fd, { timeout: 300_000 })
      .then((r) => r.data);
  },
  analyzeRevolut: (file: File, encoding = "utf-8") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("encoding", encoding);
    return api
      .post<RevolutAnalyzeResult>("/bank/revolut/analyze", fd)
      .then((r) => r.data);
  },
  importRevolut: (
    file: File,
    opts: {
      taxpayer_name?: string;
      taxpayer_id?: number;
      scan_environment_slug?: string;
      scan_environment_id?: number;
      encoding?: string;
    } = {}
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.taxpayer_name) fd.append("taxpayer_name", opts.taxpayer_name);
    if (opts.taxpayer_id != null) fd.append("taxpayer_id", String(opts.taxpayer_id));
    if (opts.scan_environment_slug)
      fd.append("scan_environment_slug", opts.scan_environment_slug);
    if (opts.scan_environment_id != null)
      fd.append("scan_environment_id", String(opts.scan_environment_id));
    fd.append("encoding", opts.encoding ?? "utf-8");
    return api
      .post<BankImportResult & { owner_acct_name?: string }>("/bank/revolut/import", fd, { timeout: 300_000 })
      .then((r) => r.data);
  },
  schema: () =>
    api
      .get<{ schema: string; allowed_tables: string[] }>("/bank/schema")
      .then((r) => r.data),
  sql: (sql: string, maxRows = 50000) =>
    api
      .post<BankSqlResult>("/bank/sql", { sql, max_rows: maxRows })
      .then((r) => r.data),
  nl2sql: (prompt: string, history?: Array<{ role: string; content: string }>) =>
    api
      .post<BankNl2SqlResult>("/bank/nl2sql", { prompt, history })
      .then((r) => r.data),
};

export interface BankItem {
  id: number;
  name: string;
  bic: string;
  blz: string | null;
  country: string;
  notes: string | null;
  accounts_count: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BankCreate {
  name: string;
  bic: string;
  blz?: string | null;
  country?: string;
  notes?: string | null;
}

export interface BankUpdate {
  name?: string;
  bic?: string;
  blz?: string | null;
  country?: string;
  notes?: string | null;
}

export const banksApi = {
  list: () => api.get<BankItem[]>("/banks").then((r) => r.data),
  create: (body: BankCreate) => api.post<BankItem>("/banks", body).then((r) => r.data),
  update: (id: number, body: BankUpdate) =>
    api.put<BankItem>(`/banks/${id}`, body).then((r) => r.data),
  delete: (id: number) =>
    api.delete<{ deleted: boolean; id: number }>(`/banks/${id}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Auswertungen — Mietkonto, Report-Templates, Nebenkostenabrechnung
// ---------------------------------------------------------------------------

export const reportsApi = {
  // --- Mietkonto ---
  objects: () =>
    api.get<ReportObject[]>("/reports/objects").then((r) => r.data),
  rentalUnits: (params?: { tax_object_id?: number; object_label?: string }) =>
    api
      .get<RentalUnit[]>("/reports/rental-units", { params })
      .then((r) => r.data),
  patchRentalUnit: (id: number, body: Partial<RentalUnit>) =>
    api
      .patch<RentalUnit>(`/reports/rental-units/${id}`, body)
      .then((r) => r.data),
  createRentalUnit: (body: Partial<RentalUnit>) =>
    api.post<RentalUnit>("/reports/rental-units", body).then((r) => r.data),
  deleteRentalUnit: (id: number) =>
    api.delete(`/reports/rental-units/${id}`).then((r) => r.data),
  linkUnitsToObject: (taxObjectId: number, objectLabel: string) =>
    api
      .post<{ linked: number; tax_object_id: number }>(
        "/reports/rental-units/link-object",
        { tax_object_id: taxObjectId, object_label: objectLabel }
      )
      .then((r) => r.data),
  ibanSuggestions: (params?: { tax_object_id?: number; object_label?: string }) =>
    api
      .get<IbanSuggestionResult>("/reports/iban-suggestions", { params })
      .then((r) => r.data),
  applyIbanSuggestion: (body: {
    contact_id: number;
    iban: string;
    bank_name?: string;
  }) =>
    api
      .post<{ created: boolean; contact_id: number; iban: string }>(
        "/reports/iban-suggestions/apply",
        body
      )
      .then((r) => r.data),
  importRentalUnits: (overwrite = false) =>
    api
      .post<{ results: unknown }>(
        "/reports/rental-units/import",
        {},
        { params: { overwrite } }
      )
      .then((r) => r.data),
  recomputeLedger: (body: {
    rental_unit_id?: number;
    tax_object_id?: number;
    object_label?: string;
    year?: number;
  }) =>
    api
      .post<{ units: number; rows: number }>(
        "/reports/rent-ledger/recompute",
        body
      )
      .then((r) => r.data),
  rentLedger: (params?: {
    tax_object_id?: number;
    object_label?: string;
    rental_unit_id?: number;
    year?: number;
  }) =>
    api
      .get<RentLedger>("/reports/rent-ledger", { params })
      .then((r) => r.data),
  rentLedgerCell: (params: {
    rental_unit_id: number;
    year: number;
    month: number;
  }) =>
    api
      .get<RentLedgerCellDetail>("/reports/rent-ledger/cell", { params })
      .then((r) => r.data),
  patchLedgerEntry: (
    id: number,
    body: {
      note?: string;
      ist_amount?: number;
      status?: string;
      matched_tx_ids?: number[];
      manual_override?: boolean;
    }
  ) =>
    api
      .patch<RentLedgerEntry>(`/reports/rent-ledger/${id}`, body)
      .then((r) => r.data),
  rentLedgerPdfUrl: (params: {
    tax_object_id?: number;
    rental_unit_id?: number;
    year?: number;
  }) => {
    const q = new URLSearchParams();
    if (params.tax_object_id != null)
      q.set("tax_object_id", String(params.tax_object_id));
    if (params.rental_unit_id != null)
      q.set("rental_unit_id", String(params.rental_unit_id));
    if (params.year != null) q.set("year", String(params.year));
    return `/api/reports/rent-ledger/pdf?${q.toString()}`;
  },
  openRentLedgerPdf: async (params: {
    tax_object_id?: number;
    object_label?: string;
    rental_unit_id?: number;
    year?: number;
  }) => {
    const res = await api.get("/reports/rent-ledger/pdf", {
      params,
      responseType: "blob",
    });
    const filename =
      extractFilename(res.headers["content-disposition"]) ||
      generateMietkontoFilename(params.object_label, params.year);
    openBlobInTab(res.data as Blob, filename);
  },
  /** Mietkontoauszug im Referenz-Layout (Sammel-/Einzelzahlung automatisch). */
  openRentLedgerMietkontoauszug: async (params: {
    tax_object_id?: number;
    object_label?: string;
    rental_unit_id?: number;
    year?: number;
  }) => {
    const res = await api.get("/reports/rent-ledger/mietkontoauszug", {
      params,
      responseType: "blob",
    });
    const filename =
      extractFilename(res.headers["content-disposition"]) ||
      generateMietkontoFilename(params.object_label, params.year);
    openBlobInTab(res.data as Blob, filename);
  },
  /** Lädt den Mietkontoauszug direkt als PDF mit Dateinamen JJJJMMDD-Objektname-Jahr.pdf herunter. */
  downloadRentLedgerMietkontoauszug: async (params: {
    tax_object_id?: number;
    object_label?: string;
    rental_unit_id?: number;
    year?: number;
  }) => {
    const res = await api.get("/reports/rent-ledger/mietkontoauszug", {
      params,
      responseType: "blob",
    });
    const filename =
      extractFilename(res.headers["content-disposition"]) ||
      generateMietkontoFilename(params.object_label, params.year);
    downloadBlob(res.data as Blob, filename);
  },
  exportRentDatev: (body: {
    rental_unit_id?: number;
    tax_object_id?: number;
    object_label?: string;
    contact_id?: number;
    year?: number;
  }) =>
    api
      .post("/reports/rent-ledger/datev", body, { responseType: "blob" })
      .then((r) => ({
        blob: r.data as Blob,
        filename:
          extractFilename(r.headers["content-disposition"]) ||
          "EXTF_Mieteingaenge.csv",
      })),
  /** Kontoblatt-Übersicht (DATEV-Stil) pro Mieter/Zahler/Objekt als JSON. */
  rentStatements: (params: {
    group_by: "mieter" | "zahler" | "objekt";
    year?: number;
    tax_object_id?: number;
    object_label?: string;
    rental_unit_id?: number;
    contact_id?: number;
  }) =>
    api
      .get<RentStatementResult>("/reports/rent-ledger/statement", { params })
      .then((r) => r.data),
  /** Öffnet die Kontoblatt-Übersicht als PDF (HTML-Fallback) in neuem Tab. */
  openRentStatementPdf: async (params: {
    group_by: "mieter" | "zahler" | "objekt";
    year?: number;
    tax_object_id?: number;
    object_label?: string;
    rental_unit_id?: number;
    contact_id?: number;
  }) => {
    const res = await api.get("/reports/rent-ledger/statement", {
      params: { ...params, format: "pdf" },
      responseType: "blob",
    });
    const filename = extractFilename(res.headers["content-disposition"]) || undefined;
    openBlobInTab(res.data as Blob, filename);
  },
  // --- Report-Templates ---
  templates: (params?: {
    report_type?: string;
    tax_object_id?: number;
    period_year?: number;
  }) =>
    api
      .get<ReportTemplate[]>("/reports/templates", { params })
      .then((r) => r.data),
  template: (id: number) =>
    api.get<ReportTemplate>(`/reports/templates/${id}`).then((r) => r.data),
  createTemplate: (body: Partial<ReportTemplate> & { key: string; name: string }) =>
    api.post<ReportTemplate>("/reports/templates", body).then((r) => r.data),
  patchTemplate: (
    id: number,
    body: {
      name?: string;
      due_amount?: number | null;
      period_year?: number;
      settlement_date?: string | null;
      rental_unit_id?: number | null;
      contact_id?: number | null;
      notes?: string;
    }
  ) =>
    api.patch<ReportTemplate>(`/reports/templates/${id}`, body).then((r) => r.data),
  uploadTemplateFile: (id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post<ReportTemplate>(`/reports/templates/${id}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  deleteTemplate: (id: number) =>
    api
      .delete<{ deleted: number }>(`/reports/templates/${id}`)
      .then((r) => r.data),
  patchField: (
    id: number,
    body: {
      value_manual?: string;
      is_overridden?: boolean;
      amount?: number;
      label?: string;
    }
  ) =>
    api
      .patch<ReportTemplateField>(`/reports/template-fields/${id}`, body)
      .then((r) => r.data),
  openTemplatePdf: async (id: number) => {
      const res = await api.get(`/reports/templates/${id}/pdf`, {
        responseType: "blob",
      });
      const filename = extractFilename(res.headers["content-disposition"]) || undefined;
      openBlobInTab(res.data as Blob, filename);
    },
  // --- Nebenkostenabrechnung ---
  createNebenkosten: (body: {
    tax_object_id: number;
    year: number;
    rental_unit_id?: number;
    months?: number;
  }) =>
    api
      .post<ReportTemplate>("/reports/nebenkosten", body)
      .then((r) => r.data),
  uploadNebenkosten: (formData: FormData) =>
    api
      .post<ReportTemplate>("/reports/nebenkosten/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Schuldverhältnisse / Verträge
// ---------------------------------------------------------------------------

export interface ContractLookup {
  id: number;
  kind:
    | "contract_type"
    | "payment_method"
    | "rhythm_unit"
    | "unit_nutzung"
    | "unit_etage"
    | "unit_lage"
    | "unit_zustand"
    | "unit_aufteilung"
    | "document_source";
  slug: string;
  label: string;
  sort_order: number;
  is_system: boolean;
  interval_months: number | null;
  interval_days: number | null;
}

export interface Contract {
  id: number;
  title: string;
  contract_type_id: number | null;
  contract_type: ContractLookup | null;
  purpose: string | null;
  debtor_contact_id: number | null;
  debtor_name: string | null;
  creditor_contact_id: number | null;
  creditor_name: string | null;
  start_date: string | null;
  end_date: string | null;
  amount: number;
  currency: string;
  rhythm_value: number;
  rhythm_unit_id: number | null;
  rhythm_unit: ContractLookup | null;
  first_due_date: string | null;
  debtor_iban: string | null;
  creditor_iban: string | null;
  payment_method_id: number | null;
  payment_method: ContractLookup | null;
  tolerance_days: number;
  statement_start_date: string | null;
  opening_balance: number;
  /** Gilt `amount` als Brutto (true) oder Netto-Kaltmiete (false)? */
  amount_is_gross: boolean;
  /** MwSt-Satz in Prozent (0/7/19 …). */
  vat_rate: number;
  /** Nebenkosten-Vorauszahlung (netto). */
  nk_net: number;
  /** Abgeleitete Aufteilung (Server-berechnet) für die Vorschau. */
  breakdown: ContractBreakdown;
  status: "aktiv" | "beendet";
  note: string | null;
  source_rental_unit_id: number | null;
  /** Etage aus der Quell-Mieteinheit (nur bei abgeleiteten Mietverträgen). */
  etage: string | null;
  payment_group_id: number | null;
  payment_group_name: string | null;
}

export interface ContractBreakdown {
  netto: number;
  nk: number;
  mwst: number;
  brutto: number;
}

export type ContractBody = Omit<
  Contract,
  | "id"
  | "contract_type"
  | "rhythm_unit"
  | "payment_method"
  | "debtor_name"
  | "creditor_name"
  | "breakdown"
  | "source_rental_unit_id"
  | "etage"
  | "payment_group_id"
  | "payment_group_name"
>;

export interface ContractAddendum {
  id: number;
  contract_id: number;
  effective_from: string;
  effective_to: string | null;
  override_amount: number | null;
  amount_is_gross: boolean | null;
  vat_rate: number | null;
  note: string | null;
}

export type ContractAddendumBody = Omit<ContractAddendum, "id" | "contract_id">;

export interface ContractGroupMember {
  id: number;
  title: string;
  amount: number;
  status: string;
}

export interface ContractGroup {
  id: number;
  name: string;
  note: string | null;
  member_count: number;
  /** Brutto-Summe aller Mitglieds-Verträge (was in EINER Zahlung kommt). */
  total_amount: number;
  members: ContractGroupMember[];
}

export interface ContractLedgerRow {
  kind: "vorsaldo" | "soll" | "zahlung";
  date: string | null;
  soll: number;
  ist: number;
  saldo: number;
  status: string | null;
  label: string | null;
  payment_id: number | null;
  /** Etage der Einheit des zugehörigen Vertrags (leer bei Gruppen-Summenzeilen). */
  etage: string | null;
  contract_id: number | null;
}

export interface ContractPayment {
  id: number;
  payment_date: string;
  amount: number;
  purpose: string | null;
  payment_method_id: number | null;
  bank_transaction_id: number | null;
  match_method: string | null;
  match_confidence: number | null;
  note: string | null;
}

export interface ContractLedger {
  contract_id?: number;
  group_id?: number;
  title: string;
  period_from: string | null;
  period_to: string;
  opening_balance: number;
  rows: ContractLedgerRow[];
  sum_soll: number;
  sum_ist: number;
  closing_balance: number;
  payments: ContractPayment[];
}

export interface SavedReportRow {
  id: number;
  report_number: string;
  report_type: string;
  report_date: string;
  period_from: string | null;
  period_to: string | null;
  params: { contract_ids?: number[]; file_format?: string } | null;
  summary: string | null;
  notes: string | null;
  file_format: string;
  has_file: boolean;
  created_by: string | null;
  created_at: string | null;
}

/** Vertragsvorschlag aus markierten Bankumsätzen (Banking-Import). */
export interface BankContractProposal {
  group_key: string;
  counterparty_name: string;
  counterparty_iban: string | null;
  owner_iban: string | null;
  owner_name: string | null;
  /** incoming = Gegenseite zahlt an uns · outgoing = wir zahlen an sie. */
  direction: "incoming" | "outgoing";
  amount: number;
  currency: string;
  rhythm_unit_id: number | null;
  rhythm_unit_slug: string | null;
  rhythm_value: number;
  rhythm_label: string;
  first_due_date: string | null;
  start_date: string | null;
  purpose: string | null;
  title: string;
  contract_type_id: number | null;
  payment_method_id: number | null;
  tolerance_days: number;
  tx_count_selected: number;
  tx_count_history: number;
  confidence: "hoch" | "mittel" | "niedrig";
  has_existing_contract: boolean;
  existing_contract_id: number | null;
  existing_contract_title: string | null;
}

/** Nutzlast beim Anlegen (nur die vom Server benötigten Felder). */
export interface BankContractProposalInput {
  counterparty_name: string;
  counterparty_iban: string | null;
  owner_iban: string | null;
  direction: "incoming" | "outgoing";
  title: string;
  amount: number;
  currency: string;
  rhythm_value: number;
  rhythm_unit_id: number | null;
  first_due_date: string | null;
  start_date: string | null;
  purpose: string | null;
  contract_type_id: number | null;
  payment_method_id: number | null;
  tolerance_days: number;
}

export const contractsApi = {
  // Lookups (konfigurierbare Auswahlwerte)
  lookups: (kind?: string) =>
    api
      .get<ContractLookup[]>("/contracts/lookups", { params: { kind } })
      .then((r) => r.data),
  createLookup: (body: Partial<ContractLookup> & { kind: string; label: string }) =>
    api.post<ContractLookup>("/contracts/lookups", body).then((r) => r.data),
  updateLookup: (id: number, body: Partial<ContractLookup> & { kind: string; label: string }) =>
    api.put<ContractLookup>(`/contracts/lookups/${id}`, body).then((r) => r.data),
  deleteLookup: (id: number) =>
    api.delete(`/contracts/lookups/${id}`).then((r) => r.data),

  // Verträge
  list: (params?: { status?: string; contract_type_id?: number; q?: string }) =>
    api.get<Contract[]>("/contracts", { params }).then((r) => r.data),
  create: (body: Partial<ContractBody> & { title: string }) =>
    api.post<Contract>("/contracts", body).then((r) => r.data),
  update: (id: number, body: Partial<ContractBody> & { title: string }) =>
    api.put<Contract>(`/contracts/${id}`, body).then((r) => r.data),
  delete: (id: number) => api.delete(`/contracts/${id}`).then((r) => r.data),
  ledger: (id: number, params?: { period_from?: string; period_to?: string }) =>
    api
      .get<ContractLedger>(`/contracts/${id}/ledger`, { params })
      .then((r) => r.data),
  matchBank: (id: number, params?: { period_from?: string; period_to?: string }) =>
    api
      .post<{ created: number; skipped: number; candidates: number }>(
        `/contracts/${id}/match-bank`,
        null,
        { params }
      )
      .then((r) => r.data),
  deriveRental: () =>
    api
      .post<{ created: number; skipped: number }>("/contracts/derive-rental")
      .then((r) => r.data),
  // Verträge aus markierten Bankumsätzen vorschlagen & anlegen.
  proposeFromBank: (transaction_ids: number[]) =>
    api
      .post<BankContractProposal[]>("/contracts/propose-from-bank", {
        transaction_ids,
      })
      .then((r) => r.data),
  createFromBank: (proposals: BankContractProposalInput[]) =>
    api
      .post<{
        created: number;
        skipped: number;
        contracts: Array<{ id: number; title: string }>;
      }>("/contracts/create-from-bank", { proposals })
      .then((r) => r.data),
  bulkStatementStart: (body: {
    contract_ids: number[];
    statement_start_date: string | null;
    opening_balance: number;
    balance_mode: "per_contract" | "group";
  }) =>
    api
      .post<{ updated: number }>("/contracts/bulk-statement-start", body)
      .then((r) => r.data),

  // Zahlungsgruppen (mehrere Verträge, eine Sammelzahlung)
  groups: () =>
    api.get<ContractGroup[]>("/contracts/groups").then((r) => r.data),
  createGroup: (body: { name: string; note?: string | null; contract_ids: number[] }) =>
    api.post<ContractGroup>("/contracts/groups", body).then((r) => r.data),
  updateGroup: (
    id: number,
    body: { name: string; note?: string | null; contract_ids?: number[] }
  ) => api.put<ContractGroup>(`/contracts/groups/${id}`, body).then((r) => r.data),
  deleteGroup: (id: number) =>
    api.delete(`/contracts/groups/${id}`).then((r) => r.data),
  groupLedger: (id: number, params?: { period_from?: string; period_to?: string }) =>
    api
      .get<ContractLedger>(`/contracts/groups/${id}/ledger`, { params })
      .then((r) => r.data),
  groupMatchBank: (id: number, params?: { period_from?: string; period_to?: string }) =>
    api
      .post<{ created: number; skipped: number; candidates: number }>(
        `/contracts/groups/${id}/match-bank`,
        null,
        { params }
      )
      .then((r) => r.data),

  // Zahlungen
  createPayment: (
    contractId: number,
    body: {
      payment_date: string;
      amount: number;
      payment_method_id?: number | null;
      purpose?: string | null;
      note?: string | null;
    }
  ) =>
    api
      .post<ContractPayment>(`/contracts/${contractId}/payments`, body)
      .then((r) => r.data),
  updatePayment: (
    paymentId: number,
    body: {
      payment_date: string;
      amount: number;
      payment_method_id?: number | null;
      purpose?: string | null;
      note?: string | null;
    }
  ) =>
    api
      .put<ContractPayment>(`/contracts/payments/${paymentId}`, body)
      .then((r) => r.data),
  deletePayment: (paymentId: number) =>
    api.delete(`/contracts/payments/${paymentId}`).then((r) => r.data),

  // Nachträge / Sondervereinbarungen
  addenda: (contractId: number) =>
    api
      .get<ContractAddendum[]>(`/contracts/${contractId}/addenda`)
      .then((r) => r.data),
  createAddendum: (contractId: number, body: ContractAddendumBody) =>
    api
      .post<ContractAddendum>(`/contracts/${contractId}/addenda`, body)
      .then((r) => r.data),
  updateAddendum: (addendumId: number, body: ContractAddendumBody) =>
    api
      .put<ContractAddendum>(`/contracts/addenda/${addendumId}`, body)
      .then((r) => r.data),
  deleteAddendum: (addendumId: number) =>
    api.delete(`/contracts/addenda/${addendumId}`).then((r) => r.data),

  // Berichte
  generateReport: (body: {
    contract_ids?: number[];
    period_from: string;
    period_to: string;
    file_format: "pdf" | "csv";
    notes?: string;
  }) => api.post<SavedReportRow>("/contracts/reports", body).then((r) => r.data),
  reports: () => api.get<SavedReportRow[]>("/contracts/reports").then((r) => r.data),
  openReportFile: async (id: number) => {
    const res = await api.get(`/contracts/reports/${id}/file`, {
      responseType: "blob",
    });
    const filename = extractFilename(res.headers["content-disposition"]) || undefined;
    openBlobInTab(res.data as Blob, filename);
  },
  deleteReport: (id: number) =>
    api.delete(`/contracts/reports/${id}`).then((r) => r.data),
};

/** Erzeugt den Dateinamen nach Schema: JJJJMMDD-Objektname-Jahr.pdf */
export function generateMietkontoFilename(objectLabel?: string, year?: number): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const todayStr = `${y}${m}${d}`;
  const raw = objectLabel || "Objekt";
  const objClean =
    raw
      .replace(/[/\\:*?"<>|.,;]/g, "")
      .trim()
      .replace(/[-\s]+/g, "_") || "Objekt";
  const yearStr = year ? String(year) : "gesamt";
  return `${todayStr}-${objClean}-${yearStr}.pdf`;
}

/** Öffnet ein Blob (PDF oder HTML-Fallback) in einem neuen Tab mit Dateinamen. */
export function openBlobInTab(blob: Blob, filename?: string): void {
  const file = filename
    ? new File([blob], filename, { type: blob.type || "application/pdf" })
    : blob;
  const url = URL.createObjectURL(file);
  const win = window.open(url, "_blank", "noopener");
  if (win && filename) {
    try {
      win.document.title = filename;
    } catch {
      // ignore
    }
  }
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    if (filename) a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

/** Lädt ein Blob direkt herunter. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ---------------------------------------------------------------------------
// Inline-Beleg-Zuordnung & ZIP-Export (Scanumgebungen-Erweiterung)
// ---------------------------------------------------------------------------

export interface CategoryFile {
  name: string;
  size: number;
  modified: string;
  is_pdf: boolean;
  mime_type: string;
}

export const inlineFilingApi = {
  /** Dateianzahl pro Kategorie-Ordner (Batch) */
  categoryFileCounts: (envId: number) =>
    api
      .get<Record<number, number>>(`/scan-environments/${envId}/file-counts`)
      .then((r) => r.data),

  /** Dokumente die einer Kategorie zugeordnet sind */
  categoryDocuments: (envId: number, catId: number) =>
    api
      .get<any[]>(`/scan-environments/${envId}/categories/${catId}/documents`)
      .then((r) => r.data),

  /**
   * Kandidaten-Dokumente für eine Kategorie.
   *
   * Mit `q` wird strikt gefiltert: geliefert werden ausschließlich Dokumente,
   * die *alle* eingegebenen Begriffe enthalten (inkl. OCR-Volltext).
   * `scope` steuert den Suchraum: "tenant" = gesamte Postbox, "env" = nur
   * diese Scanumgebung.
   */
  categoryCandidates: (
    envId: number,
    catId: number,
    q?: string,
    scope: "tenant" | "env" = "tenant"
  ) =>
    api
      .get<any[]>(`/scan-environments/${envId}/categories/${catId}/candidates`, {
        params: { ...(q ? { q } : {}), scope },
      })
      .then((r) => r.data),

  /**
   * KI-Suche: Freitext → Verständnis + schreibgeschütztes SELECT → Belege.
   *
   * Nutzt denselben Weg wie die Bankumsatz-Suche (Mistral + validiertes
   * Read-only-SQL). `understanding` und `sql` werden mitgeliefert, damit
   * nachvollziehbar bleibt, wonach gesucht wurde. Mit `sql` kann eine
   * geprüfte/korrigierte Abfrage direkt ausgeführt werden.
   */
  aiSearchCandidates: (
    envId: number,
    catId: number,
    prompt: string,
    sql?: string
  ) =>
    api
      .post<{
        understanding: string;
        sql: string;
        model: string;
        /** "ki" = Treffer der KI-Abfrage, "fallback" = genaue Suche. */
        source: "ki" | "fallback";
        truncated: boolean;
        total: number;
        items: any[];
      }>(`/scan-environments/${envId}/categories/${catId}/candidates/ai`, {
        prompt,
        ...(sql ? { sql } : {}),
      })
      .then((r) => r.data),

  /** Dokument einer Kategorie zuordnen (+ Auto-Filing) */
  assignDocument: (
    envId: number,
    catId: number,
    docId: number,
    body?: { tax_object_id?: number }
  ) =>
    api
      .post<any>(
        `/scan-environments/${envId}/categories/${catId}/assign/${docId}`,
        body || {}
      )
      .then((r) => r.data),

  /** ZIP-Export der gesamten Ordnerstruktur */
  exportZip: (envId: number, taxpayerId?: number) => {
    const params = taxpayerId ? { taxpayer_id: taxpayerId } : undefined;
    return api
      .get(`/scan-environments/${envId}/export`, {
        params,
        responseType: "blob",
      })
      .then((r) => ({
        blob: r.data as Blob,
        filename:
          extractFilename(r.headers["content-disposition"]) ||
          `export_${envId}.zip`,
      }));
  },

  // --- Kategorie-Dateimanager ---

  /** Dateien im Kategorie-Ordner auflisten */
  categoryFiles: (envId: number, catId: number, taxObjectId?: number) =>
    api
      .get<{ folder: string; exists: boolean; files: CategoryFile[] }>(
        `/scan-environments/${envId}/categories/${catId}/files`,
        { params: taxObjectId ? { tax_object_id: taxObjectId } : undefined }
      )
      .then((r) => r.data),

  /** Datei in den Kategorie-Ordner hochladen */
  uploadCategoryFile: (
    envId: number,
    catId: number,
    file: File,
    taxObjectId?: number
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    const params: Record<string, unknown> = {};
    if (taxObjectId) params.tax_object_id = taxObjectId;
    return api
      .post<{ name: string; size: number; path: string; message: string }>(
        `/scan-environments/${envId}/categories/${catId}/files`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params,
        }
      )
      .then((r) => r.data);
  },

  /** Datei im Kategorie-Ordner umbenennen */
  renameCategoryFile: (
    envId: number,
    catId: number,
    oldName: string,
    newName: string,
    taxObjectId?: number
  ) =>
    api
      .put<{ old_name: string; new_name: string; path: string; message: string }>(
        `/scan-environments/${envId}/categories/${catId}/files/rename`,
        { old_name: oldName, new_name: newName },
        { params: taxObjectId ? { tax_object_id: taxObjectId } : undefined }
      )
      .then((r) => r.data),

  /** Datei im Kategorie-Ordner löschen */
  deleteCategoryFile: (
    envId: number,
    catId: number,
    filename: string,
    taxObjectId?: number
  ) =>
    api
      .delete<{ filename: string; message: string }>(
        `/scan-environments/${envId}/categories/${catId}/files`,
        {
          data: { filename },
          params: taxObjectId ? { tax_object_id: taxObjectId } : undefined,
        }
      )
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Bank-Suggestions (KI-Kontobewegung → Kategorie)
// ---------------------------------------------------------------------------

export const bankSuggestionsApi = {
  /** KI-Vorschläge generieren */
  generate: (body: {
    scan_environment_id: number;
    taxpayer_id: number;
    limit?: number;
  }) =>
    api
      .post<any>("/bank-suggestions/generate", body)
      .then((r) => r.data),

  /** Vorschläge auflisten */
  list: (params?: {
    scan_environment_id?: number;
    taxpayer_id?: number;
    status?: string;
    min_confidence?: number;
    sort?: string;
    page?: number;
    per_page?: number;
  }) =>
    api
      .get<any>("/bank-suggestions", { params })
      .then((r) => r.data),

  /** Vorschlag akzeptieren */
  accept: (id: number) =>
    api.put<any>(`/bank-suggestions/${id}/accept`).then((r) => r.data),

  /** Vorschlag ablehnen */
  reject: (id: number) =>
    api.put<any>(`/bank-suggestions/${id}/reject`).then((r) => r.data),

  /** Batch-Accept/Reject */
  batch: (body: { ids: number[]; action: "accept" | "reject" }) =>
    api.post<any>("/bank-suggestions/batch", body).then((r) => r.data),

  /** Statistiken */
  stats: (params?: {
    scan_environment_id?: number;
    taxpayer_id?: number;
  }) =>
    api
      .get<any>("/bank-suggestions/stats", { params })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Advisor Portal (Steuerberater-Links)
// ---------------------------------------------------------------------------

export const advisorApi = {
  /** Share-Link erstellen */
  createLink: (body: {
    scan_environment_id: number;
    taxpayer_id?: number;
    label?: string;
    expires_in_days?: number;
    max_downloads?: number;
  }) =>
    api
      .post<any>("/advisor/links", body)
      .then((r) => r.data),

  /** Alle Links auflisten */
  listLinks: () =>
    api.get<any[]>("/advisor/links").then((r) => r.data),

  /** Link deaktivieren */
  deleteLink: (id: number) =>
    api.delete(`/advisor/links/${id}`).then((r) => r.data),

  /** Öffentliche Ansicht (kein Auth) */
  view: (token: string) =>
    api.get<any>(`/advisor/view/${token}`).then((r) => r.data),

  /** ZIP-Download über Token (kein Auth) */
  download: (token: string) =>
    api
      .get(`/advisor/view/${token}/download`, { responseType: "blob" })
      .then((r) => ({
        blob: r.data as Blob,
        filename:
          extractFilename(r.headers["content-disposition"]) ||
          `steuerberater_export.zip`,
      })),
};

// ---------------------------------------------------------------------------
// KI-Assistent & Universelle Suche (Phase 5 / KIANALYSE)
// ---------------------------------------------------------------------------

export const searchApi = {
  search: (params: { q?: string; domains?: string; limit?: number; page?: number; sort?: string }) =>
    api.get<SearchResponse>("/search", { params }).then((r) => r.data),
  explain: (q: string, domain?: string) =>
    api.get<SearchExplain>("/search/explain", { params: { q, domain } }).then((r) => r.data),
  help: () =>
    api.get<SearchHelp>("/search/help").then((r) => r.data),
  export: (body: { q: string; domains?: string[]; format: string; title?: string; save?: boolean }) =>
    api.post("/search/export", body, { responseType: "blob" }).then((r) => {
      const filename = extractFilename(r.headers["content-disposition"]) || `suche_${body.format}`;
      downloadBlob(r.data as Blob, filename);
    }),
};

export const assistantApi = {
  providers: () =>
    api.get<AssistantProviderInfo[]>("/assistant/providers").then((r) => r.data),
  conversations: (params?: { q?: string; user_id?: number; page?: number; per_page?: number }) =>
    api.get<{ items: AssistantConversationSummary[]; total: number; page: number; per_page: number }>("/assistant/conversations", { params }).then((r) => r.data),
  createConversation: (body: { title?: string; provider?: string; model?: string; scope?: AssistantScope }) =>
    api.post<{ id: number; title: string; provider: string; model: string; created_at: string }>("/assistant/conversations", body).then((r) => r.data),
  getConversation: (id: number) =>
    api.get<AssistantConversationDetail>(`/assistant/conversations/${id}`).then((r) => r.data),
  patchConversation: (id: number, body: { title?: string; pinned?: boolean; scope?: AssistantScope; provider?: string; model?: string }) =>
    api.patch<{ ok: boolean; id: number; title: string; pinned: boolean }>(`/assistant/conversations/${id}`, body).then((r) => r.data),
  deleteConversations: (ids: number[], deleteFiles: boolean = false) =>
    api.delete<{ deleted_conversations: number; deleted_files: number }>("/assistant/conversations", { data: { ids, delete_files: deleteFiles } }).then((r) => r.data),
  sendMessage: (conversationId: number, body: { content: string; provider?: string; model?: string; scope?: AssistantScope }) =>
    api.post<{ run_id: number | null; user_message_id: number; assistant_message_id: number; is_slash_command: boolean }>(`/assistant/conversations/${conversationId}/messages`, body).then((r) => r.data),
  getRunStatus: (runId: number, params?: { since_step?: number; content_from?: number }) =>
    api.get<AssistantRunState>(`/assistant/runs/${runId}`, { params }).then((r) => r.data),
  cancelRun: (runId: number) =>
    api.post<{ ok: boolean }>(`/assistant/runs/${runId}/cancel`).then((r) => r.data),
  confirmAction: (actionId: number) =>
    api.post<{ ok: boolean; action_id: number; status: string; summary: string }>(`/assistant/actions/${actionId}/confirm`).then((r) => r.data),
  rejectAction: (actionId: number, reason?: string) =>
    api.post<{ ok: boolean; action_id: number; status: string }>(`/assistant/actions/${actionId}/reject`, { reason }).then((r) => r.data),
  files: (params?: { conversation_id?: number; kind?: string; q?: string; page?: number; per_page?: number }) =>
    api.get<{ items: AssistantFile[]; total: number; page: number; per_page: number }>("/assistant/files", { params }).then((r) => r.data),
  downloadFile: async (fileId: number, filename?: string) => {
    const res = await api.get(`/assistant/files/${fileId}/download`, { responseType: "blob" });
    const fn = filename || extractFilename(res.headers["content-disposition"]) || `datei_${fileId}`;
    downloadBlob(res.data as Blob, fn);
  },
  getFileContent: (fileId: number) =>
    api.get<{ id: number; kind: string; content: string }>(`/assistant/files/${fileId}/content`).then((r) => r.data),
  deleteFiles: (ids: number[]) =>
    api.delete<{ deleted: number }>("/assistant/files", { data: { ids } }).then((r) => r.data),
  quickPrompts: () =>
    api.get<Array<{ title: string; prompt: string; icon: string }>>("/assistant/quick-prompts").then((r) => r.data),
  exportMessage: (messageId: number, body: { format: string; title?: string; include_steps?: boolean; include_prompt?: boolean; save?: boolean }) =>
    api.post(`/assistant/messages/${messageId}/export`, body, { responseType: "blob" }).then((r) => {
      const filename = extractFilename(r.headers["content-disposition"]) || `nachricht_${messageId}.${body.format}`;
      downloadBlob(r.data as Blob, filename);
    }),
  exportStep: (stepId: number, body: { format: string; title?: string; save?: boolean }) =>
    api.post(`/assistant/steps/${stepId}/export`, body, { responseType: "blob" }).then((r) => {
      const filename = extractFilename(r.headers["content-disposition"]) || `schritt_${stepId}.${body.format}`;
      downloadBlob(r.data as Blob, filename);
    }),
  exportConversation: (conversationId: number, body: { format: string; include_steps?: boolean; save?: boolean }) =>
    api.post(`/assistant/conversations/${conversationId}/export`, body, { responseType: "blob" }).then((r) => {
      const filename = extractFilename(r.headers["content-disposition"]) || `gespraech_${conversationId}.${body.format}`;
      downloadBlob(r.data as Blob, filename);
    }),
};

export const receiptLinksApi = {
  list: (params?: { status?: string; transaction_id?: number; document_id?: number; min_confidence?: number; page?: number; per_page?: number }) =>
    api.get<{ items: ReceiptLink[]; total: number; page: number; per_page: number }>("/receipt-links", { params }).then((r) => r.data),
  suggest: (body: { transaction_ids?: number[]; bank_import_id?: number; date_from?: string; date_to?: string; owner_iban?: string; limit_per_transaction?: number }) =>
    api.post<{ processed: number; suggested: number; results: Record<number, ReceiptCandidate[]> }>("/receipt-links/suggest", body).then((r) => r.data),
  confirm: (id: number) =>
    api.put<{ ok: boolean; id: number; status: string }>(`/receipt-links/${id}/confirm`).then((r) => r.data),
  reject: (id: number) =>
    api.put<{ ok: boolean; id: number; status: string }>(`/receipt-links/${id}/reject`).then((r) => r.data),
  createManual: (body: { transaction_id: number; document_id: number; note?: string }) =>
    api.post<{ ok: boolean; id: number; status: string }>("/receipt-links", body).then((r) => r.data),
  delete: (id: number) =>
    api.delete<{ deleted: boolean }>(`/receipt-links/${id}`).then((r) => r.data),
  transactionCandidates: (txId: number) =>
    api.get<ReceiptCandidate[]>(`/receipt-links/transactions/${txId}/candidates`).then((r) => r.data),
  documentCandidates: (docId: number) =>
    api.get<any[]>(`/receipt-links/documents/${docId}/candidates`).then((r) => r.data),
};
